import { Lettermark, Logo } from "@repo/ui/components/logo";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui/components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@repo/ui/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@repo/ui/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@repo/ui/components/ui/sidebar";
import { cn } from "@repo/ui/lib/utils";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { ChevronsUpDown, LogOut, type LucideIcon, Settings, Terminal } from "lucide-react";
import type { ComponentProps } from "react";
import { useState } from "react";
import { type ActivePatientLink, ActivePatientsAction, NavQuickActions } from "./nav-quick-actions";
export interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
}

export interface UserData {
  name: string;
  email: string;
  initials: string;
}

export interface NavUserProps {
  user: UserData;
  theme: "light" | "dark";
  onThemeChange: (theme: "light" | "dark") => void;
  onLogout: () => void;
  onOpenCommandPalette: () => void;
}

function NavUser({ user, theme, onThemeChange, onLogout, onOpenCommandPalette }: NavUserProps) {
  const { isMobile } = useSidebar();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <SidebarMenuButton size="lg" render={<DropdownMenuTrigger />}>
              <Avatar>
                <AvatarFallback>{user.initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
            <DropdownMenuContent
              className="w-[var(--anchor-width)] min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuGroup>
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg">{user.initials}</AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">{user.name}</span>
                      <span className="truncate text-xs">{user.email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={onOpenCommandPalette}>
                  <Terminal />
                  <span>Command Palette</span>
                  <span className="ml-auto text-xs tracking-widest text-muted-foreground">⇧⌘P</span>
                </DropdownMenuItem>
                <DropdownMenuItem render={<Link to="/settings" />}>
                  <Settings />
                  <span>Settings</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Toggle Theme</DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem onClick={() => onThemeChange("light")}>
                        <span
                          className={cn(
                            "size-2 rounded-full",
                            theme === "light" ? "bg-primary" : "bg-transparent border",
                          )}
                        />
                        Light
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onThemeChange("dark")}>
                        <span
                          className={cn(
                            "size-2 rounded-full",
                            theme === "dark" ? "bg-primary" : "bg-transparent border",
                          )}
                        />
                        Dark
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => setShowLogoutDialog(true)}>
                  <LogOut />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
            <AlertDialogDescription>You will be redirected to the login page.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onLogout}>Log out</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export interface AppSidebarProps extends ComponentProps<typeof Sidebar> {
  navItems: NavItem[];
  adminItem?: NavItem;
  activePatients: ActivePatientLink[];
  user: UserData;
  theme: "light" | "dark";
  onThemeChange: (theme: "light" | "dark") => void;
  onLogout: () => void;
  onOpenCommandPalette: () => void;
}

export function AppSidebar({
  navItems,
  adminItem,
  activePatients,
  user,
  theme,
  onThemeChange,
  onLogout,
  onOpenCommandPalette,
  ...props
}: AppSidebarProps) {
  const matchRoute = useMatchRoute();

  // Filter out settings from navItems if present
  const mainNavItems = navItems.filter((item) => item.url !== "/settings");

  return (
    <Sidebar collapsible="icon" variant="inset" {...props}>
      {/*
        Brand sits above the nav and shares its optical grid: expanded, the
        lockup starts on the same left edge as the nav icons; collapsed, the
        mark is centred in a box the size of a nav button, so it lands on the
        same axis as the icon column.
      */}
      <SidebarHeader className="items-start px-4 pt-3 pb-1 group-data-[collapsible=icon]:px-2">
        <Logo variant="adaptive" className="h-6 group-data-[collapsible=icon]:hidden" title={null} />
        {/* size-8 + the nav group's 1px inset reproduces a nav button's box exactly. */}
        <div className="ml-px hidden size-8 items-center justify-center group-data-[collapsible=icon]:flex">
          <Lettermark variant="adaptive" className="w-7" title={null} />
        </div>
        <span className="sr-only">MindForge</span>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent className="px-1.5 md:px-0 group-data-[collapsible=icon]:px-px transition-[padding] duration-200 ease-linear">
            <SidebarMenu>
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    render={<Link to={item.url} activeProps={{ "data-active": "true" }} />}
                    tooltip={{
                      children: item.title,
                      hidden: false,
                    }}
                    className="px-2.5 md:px-2"
                  >
                    <item.icon />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <NavQuickActions>
          <ActivePatientsAction patients={activePatients} />
        </NavQuickActions>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          {adminItem && (
            <SidebarMenuItem>
              <Link to={adminItem.url}>
                <SidebarMenuButton
                  tooltip={{
                    children: adminItem.title,
                    hidden: false,
                  }}
                  isActive={!!matchRoute({ to: adminItem.url, fuzzy: true })}
                  className="px-2.5 md:px-2"
                >
                  <adminItem.icon />
                  <span>{adminItem.title}</span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
        <NavUser
          user={user}
          theme={theme}
          onThemeChange={onThemeChange}
          onLogout={onLogout}
          onOpenCommandPalette={onOpenCommandPalette}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
